/** Pieces of the chat screen: answer text, the cards under an answer, and icons. */
import { useState, type ReactNode } from 'react';
import { parseCards, type Card } from '../../supabase/functions/_shared/assistant/cards.ts';
import { Loaded, ScrollTable } from './components.tsx';
import { useApp } from './context.tsx';
import { adCopyText, briefText, orientationLabel } from './lib/cardText.ts';
import { cardKey } from './lib/chat.ts';
import { parseMarkdown, type Inline } from './lib/markdown.ts';
import {
  kindLabel,
  loadProposalRows,
  ProposalCommentary,
  ProposalFiguresView,
  StoredProposal,
} from './proposalParts.tsx';
import { supabase } from './supabase.ts';
import { useLoad } from './useLoad.ts';

function InlineText(props: { readonly inline: readonly Inline[] }) {
  return (
    <>
      {props.inline.map((part, i) =>
        part.bold ? <strong key={i}>{part.text}</strong> : <span key={i}>{part.text}</span>,
      )}
    </>
  );
}

/** An answer's text as paragraphs, lists and tables — never as HTML. */
/** `label` names a scrollable table for screen readers (the answer it belongs to). */
export function AnswerText(props: { readonly text: string; readonly tableLabel: string }) {
  const label = props.tableLabel;
  return (
    <div className="answer">
      {parseMarkdown(props.text).map((block, i) => {
        switch (block.type) {
          case 'paragraph':
            return (
              <p key={i}>
                <InlineText inline={block.inline} />
              </p>
            );
          case 'heading':
            return (
              <h3 key={i}>
                <InlineText inline={block.inline} />
              </h3>
            );
          case 'list': {
            const items = block.items.map((item, j) => (
              <li key={j}>
                <InlineText inline={item} />
              </li>
            ));
            return block.ordered ? <ol key={i}>{items}</ol> : <ul key={i}>{items}</ul>;
          }
          case 'table':
            return (
              <ScrollTable key={i} label={label}>
                <table>
                  <thead>
                    <tr>
                      {block.header.map((cell, j) => (
                        <th key={j} scope="col">
                          <InlineText inline={cell} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, j) => (
                      <tr key={j}>
                        {row.map((cell, k) => (
                          <td key={k}>
                            <InlineText inline={cell} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollTable>
            );
        }
      })}
    </div>
  );
}

type Status = { readonly kind: 'idle' | 'busy' | 'done' | 'failed' };

/**
 * A proposal under an answer. A live card reads its stored row (figures, status, stage approval)
 * every time it is shown; a trial card shows the figures it was computed with and no buttons.
 */
function ProposalCardView(props: { readonly card: Extract<Card, { type: 'proposal' }> }) {
  const { t } = useApp();
  const { card } = props;
  if (card.mock) {
    const kind = kindLabel(card.kind);
    return (
      <section className="chat-card trial">
        <span className="label">
          {t('cardProposal')} <span className="badge">{t('chatTrialShort')}</span>
        </span>
        <h3>
          {kind !== null && <span className="muted">{t(kind)}: </span>}
          {card.title}
        </h3>
        <ProposalFiguresView figures={card.figures} />
        <ProposalCommentary summary={card.summary} rationale={card.rationale} />
        <p className="small muted">{t('cardTrialNotApprovable')}</p>
      </section>
    );
  }
  return <LiveProposalCard proposalId={card.proposalId} />;
}

function LiveProposalCard(props: { readonly proposalId: string }) {
  const { t } = useApp();
  const [state, reload] = useLoad(async () => ({
    rows: await loadProposalRows([props.proposalId]),
    loadedAt: Date.now(),
  }));
  return (
    <section className="chat-card">
      <span className="label">{t('cardProposal')}</span>
      <Loaded state={state} reload={reload}>
        {({ rows, loadedAt }) => {
          const row = rows[0];
          return row === undefined ? (
            <p className="notice error">{t('figUnavailable')}</p>
          ) : (
            <StoredProposal row={row} now={loadedAt} onDecided={reload} withNote={false} />
          );
        }}
      </Loaded>
    </section>
  );
}

function DecisionCardView(props: {
  readonly card: Extract<Card, { type: 'decision' }>;
  /** The answer this card belongs to and its place under it: a card is saved once. */
  readonly messageId: number | null;
  readonly cardIndex: number;
  readonly alreadySaved: boolean;
}) {
  const { t, role } = useApp();
  const { card } = props;
  const [status, setStatus] = useState<Status>({ kind: props.alreadySaved ? 'done' : 'idle' });
  const save = async () => {
    if (props.messageId === null) return;
    setStatus({ kind: 'busy' });
    let branchId: string | undefined;
    if (card.branchCode !== null) {
      const branch = await supabase
        .from('branches')
        .select('id')
        .eq('code', card.branchCode)
        .maybeSingle();
      if (branch.error || !branch.data) {
        setStatus({ kind: 'failed' });
        return;
      }
      branchId = branch.data.id;
    }
    const { error } = await supabase.rpc('save_decision', {
      p_topic: card.topic,
      p_decision: card.decision,
      p_reason: card.reason,
      p_source_message_id: props.messageId,
      p_source_card: props.cardIndex,
      ...(branchId ? { p_branch_id: branchId } : {}),
    });
    // 23505: this card was saved already (another tab or device) — that is the state we want.
    setStatus({ kind: !error || error.code === '23505' ? 'done' : 'failed' });
  };
  return (
    <section className="chat-card">
      <span className="label">{t('cardDecision')}</span>
      <h3>{card.decision}</h3>
      <p className="small">
        <span className="muted">{t('cardReason')}: </span>
        {card.reason}
      </p>
      {role === 'owner' && props.messageId !== null && status.kind !== 'done' && (
        <div className="actions">
          <button
            type="button"
            className="button primary"
            disabled={status.kind === 'busy'}
            onClick={() => void save()}
          >
            {t('cardSaveDecision')}
          </button>
        </div>
      )}
      {status.kind === 'done' && <p className="notice">{t('cardDecisionSaved')}</p>}
      {status.kind === 'failed' && <p className="notice error">{t('cardSaveFailed')}</p>}
    </section>
  );
}

const OUTCOME_KEY = {
  met: 'outcomeMet',
  partly_met: 'outcomePartly',
  missed: 'outcomeMissed',
} as const;

function LessonCardView(props: { readonly card: Extract<Card, { type: 'lesson' }> }) {
  const { t } = useApp();
  return (
    <section className="chat-card">
      <span className="label">
        {t('cardLesson')} <span className="badge">{t(OUTCOME_KEY[props.card.outcome])}</span>
      </span>
      <h3>{props.card.campaign}</h3>
      <p className="small">{props.card.goal}</p>
      <a href="#/memory">{t('cardOpenLesson')}</a>
    </section>
  );
}

/** Copies a card's plain text; says so, or says to copy by hand when the browser refuses. */
function CopyButton(props: { readonly text: string }) {
  const { t } = useApp();
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  return (
    <div className="actions">
      <button
        type="button"
        className="button quiet"
        onClick={() => {
          navigator.clipboard.writeText(props.text).then(
            () => {
              setState('copied');
            },
            () => {
              setState('failed');
            },
          );
        }}
      >
        {t('copyText')}
      </button>
      {state === 'copied' && <span className="small muted">{t('copied')}</span>}
      {state === 'failed' && <span className="small error-text">{t('copyFailed')}</span>}
    </div>
  );
}

function AdCopyCardView(props: { readonly card: Extract<Card, { type: 'ad_copy' }> }) {
  const { t, language } = useApp();
  const text = adCopyText(props.card, language);
  return (
    <section className="chat-card">
      <span className="label">{t('cardAdCopy')}</span>
      <p className="pre small" dir="auto">
        {text}
      </p>
      <CopyButton text={text} />
    </section>
  );
}

function BriefCardView(props: { readonly card: Extract<Card, { type: 'brief' }> }) {
  const { t, language } = useApp();
  const { card } = props;
  return (
    <section className="chat-card">
      <span className="label">{t('cardBrief')}</span>
      <h3>{card.title}</h3>
      <p className="small">{card.goal}</p>
      <ol className="small">
        {card.shots.map((shot, i) => (
          <li key={i}>
            {shot.subject} —{' '}
            <span className="muted">{orientationLabel(shot.orientation, language)}</span>
            <br />
            <span className="muted">{shot.notes}</span>
          </li>
        ))}
      </ol>
      <CopyButton text={briefText(card, language)} />
    </section>
  );
}

export function Cards(props: {
  readonly raw: unknown;
  readonly messageId: number | null;
  readonly saved: ReadonlySet<string>;
}) {
  return (
    <>
      {parseCards(props.raw).map((card, i) => {
        switch (card.type) {
          case 'proposal':
            return <ProposalCardView key={i} card={card} />;
          case 'decision':
            return (
              <DecisionCardView
                key={i}
                card={card}
                messageId={props.messageId}
                cardIndex={i}
                alreadySaved={
                  props.messageId !== null && props.saved.has(cardKey(props.messageId, i))
                }
              />
            );
          case 'lesson':
            return <LessonCardView key={i} card={card} />;
          case 'ad_copy':
            return <AdCopyCardView key={i} card={card} />;
          case 'brief':
            return <BriefCardView key={i} card={card} />;
        }
      })}
    </>
  );
}

function Icon(props: { readonly children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {props.children}
    </svg>
  );
}
export const MicIcon = () => (
  <Icon>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </Icon>
);
export const StopIcon = () => (
  <Icon>
    <rect x="7" y="7" width="10" height="10" rx="2" />
  </Icon>
);
/** Points left in Arabic (the reading direction) and right in English. */
export const SendIcon = (props: { readonly rtl: boolean }) => (
  <Icon>
    <path d={props.rtl ? 'M20 12H5m6-6-6 6 6 6' : 'M4 12h15m-6-6 6 6-6 6'} />
  </Icon>
);
export const PlusIcon = () => (
  <Icon>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);
export const ListIcon = () => (
  <Icon>
    <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
  </Icon>
);
export const SpeakerIcon = () => (
  <Icon>
    <path d="M4 10v4h4l5 4V6L8 10H4zM16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" />
  </Icon>
);
